import { useContext, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
  Col,
  Collapse,
  CardHeader,
} from 'reactstrap';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Line, Bar, Bubble } from 'react-chartjs-2';
import {
  computeUserAdoption,
  formatLastUsedPretty,
  daysDiffFromToday,
  normalizeTimestampToDate,
  adoptionTooltipText,
  getUniqueProfessions,
  getProfessionFilterOptions,
  filterUsersByProfession,
} from '../../utils/Adoption.js';
import AdoptionReports from './AdoptionReports';

const AdoptionDashboard = ({ apiClient }) => {
  const [users, setUsers] = useState([]);
  const [usageData, setUsageData] = useState({});
  // True while a report request is being started
  const [loading, setLoading] = useState(false);
  // Bumped after starting a report so the Recent Reports list reloads
  const [reportsReloadKey, setReportsReloadKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Users a loaded background report could not load (report.failedUsers)
  const [reportFailedUsers, setReportFailedUsers] = useState([]);

  // Bulk labels modal state
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [labelsCsvData, setLabelsCsvData] = useState([]);
  const [labelsProcessing, setLabelsProcessing] = useState(false);

  // Date range state - default to last 30 days
  const getDefaultDates = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 90);
    return { startDate: thirtyDaysAgo, endDate: now };
  };

  const [dateRange, setDateRange] = useState(getDefaultDates());
  const [cachedDateRange, setCachedDateRange] = useState(null);

  // Label filter state
  const [selectedLabels, setSelectedLabels] = useState([]);
  // Profession filter state ('' = all professions)
  const [selectedProfession, setSelectedProfession] = useState('');

  // Collapse state for UI sections
  const [collapseFilters, setCollapseFilters] = useState(true);
  const [collapseStats, setCollapseStats] = useState(true);
  const [collapseBreakdown, setCollapseBreakdown] = useState(true);
  const [collapseLabelSummary, setCollapseLabelSummary] = useState(true);
  const [collapseCalendar, setCollapseCalendar] = useState(true);
  const [collapseUserDetails, setCollapseUserDetails] = useState(true);
  const [collapseCharts, setCollapseCharts] = useState(false);

  // Chart period: 'daily' | 'weekly' | 'monthly'
  const [chartPeriod, setChartPeriod] = useState('weekly');

  // Heatmap noise filter: hide bubbles with count < 10% of the max
  const [hideHeatmapNoise, setHideHeatmapNoise] = useState(true);

  // Fullscreen chart: null | 'onboarding' | 'usage' | 'adoption'
  const [fullscreenChart, setFullscreenChart] = useState(null);

  // Detail panel shown when a data point is clicked in fullscreen
  const [chartClickDetail, setChartClickDetail] = useState(null);

  // Resizable detail panel
  const [detailPanelWidth, setDetailPanelWidth] = useState(340);
  const isDraggingRef = useRef(false);
  const importFileRef = useRef(null);

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (moveEvent) => {
      if (!isDraggingRef.current) return;
      const newWidth = window.innerWidth - moveEvent.clientX;
      setDetailPanelWidth(Math.max(200, Math.min(700, newWidth)));
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Calendar view state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarViewMode, setCalendarViewMode] = useState('labels'); // 'labels' or 'users'

  // Cache key and expiry (24 hours)
  const CACHE_KEY = 'adoption_usage_data';
  const CACHE_EXPIRY_KEY = 'adoption_usage_data_expiry';
  const CACHE_DATE_RANGE_KEY = 'adoption_date_range';

  // Get cached data (check both localStorage and sessionStorage)
  const getCachedData = () => {
    try {
      // Try localStorage first, then sessionStorage
      const storages = [localStorage, sessionStorage];

      for (const storage of storages) {
        const expiry = storage.getItem(CACHE_EXPIRY_KEY);
        const now = Date.now();

        if (expiry && now < parseInt(expiry)) {
          const cached = storage.getItem(CACHE_KEY);
          const cachedRange = storage.getItem(CACHE_DATE_RANGE_KEY);

          if (cached && cachedRange) {
            const data = JSON.parse(cached);
            const range = JSON.parse(cachedRange);

            setUsageData(data.usageData || {});
            setLastUpdated(data.lastUpdated || null);
            setCachedDateRange({
              startDate: new Date(range.startDate),
              endDate: new Date(range.endDate),
            });

            const storageType =
              storage === localStorage ? 'localStorage' : 'sessionStorage';
            console.log(`Cache loaded from ${storageType}`);
            return true;
          }
        }
      }
    } catch (error) {
      console.error('Error reading cache:', error);
    }
    return false;
  };

  // Clear cache (from both storage locations)
  const clearCache = () => {
    // Clear from both localStorage and sessionStorage
    [localStorage, sessionStorage].forEach((storage) => {
      storage.removeItem(CACHE_KEY);
      storage.removeItem(CACHE_EXPIRY_KEY);
      storage.removeItem(CACHE_DATE_RANGE_KEY);
    });

    setUsageData({});
    setLastUpdated(null);
    setCachedDateRange(null);
    setReportFailedUsers([]);
    toast.info('Cache cleared');
  };

  // Load a parsed .cpta payload (an imported file or a background report)
  const loadCptaPayload = (payload, name) => {
    if (!payload.version || !payload.usageData) {
      toast.error('Invalid .cpta file');
      return;
    }
    setUsageData(payload.usageData);
    setLastUpdated(payload.lastUpdated || null);
    if (payload.dateRange) {
      const range = {
        startDate: new Date(payload.dateRange.startDate),
        endDate: new Date(payload.dateRange.endDate),
      };
      setCachedDateRange(range);
      setDateRange(range);
    }
    setReportFailedUsers(payload.report?.failedUsers || []);
    toast.success(`Data imported from ${name}`);
  };

  // Import data from a .cpta file
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        loadCptaPayload(JSON.parse(evt.target.result), file.name);
      } catch {
        toast.error('Failed to parse .cpta file');
      }
    };
    reader.readAsText(file);
  };

  const localDay = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // What "Generate Report" asks the server for: the active users matching the
  // label filter, over the selected date range.
  const buildReportRequest = () => {
    const activeUsers = filterUsersByLabels(users).filter((u) => u.isActive);
    if (activeUsers.length === 0) {
      toast.warning('No users match the current filters');
      return null;
    }
    return {
      t1: dateRange.startDate.toISOString().split('.')[0],
      t2: dateRange.endDate.toISOString().split('.')[0],
      users: activeUsers.map((u) => u.email),
      labels: selectedLabels,
      dateRange: {
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
        // Local calendar days, for the report's email and file name
        startDay: localDay(dateRange.startDate),
        endDay: localDay(dateRange.endDate),
      },
    };
  };

  // Start a background report for the current filters. It is built on the
  // server, emailed when ready, and listed under Recent Reports.
  const generateReport = async () => {
    const request = buildReportRequest();
    if (!request) return;
    setLoading(true);
    try {
      const data = await apiClient.startAdoptionReport(request);
      if (data?.jobId) {
        toast.success(
          "Report started. We'll email you when it's ready, and it will appear under Recent Reports.",
        );
        setReportsReloadKey((key) => key + 1);
      } else {
        toast.error(data?.error || 'Could not start the report.');
      }
    } catch (error) {
      toast.error('Could not start the report.');
    } finally {
      setLoading(false);
    }
  };

  // Check if we need to fetch new data based on date range
  const needsNewData = (newRange) => {
    if (!cachedDateRange) return true;

    // If new range extends beyond cached range, we need new data
    const needsEarlier = newRange.startDate < cachedDateRange.startDate;
    const needsLater = newRange.endDate > cachedDateRange.endDate;

    return needsEarlier || needsLater;
  };

  // Check if the cached data is stale (>4 hours old)
  const isDataStale = () => {
    if (!lastUpdated) return false;
    const fourHoursInMs = 4 * 60 * 60 * 1000;
    const timeSinceUpdate = Date.now() - lastUpdated;
    return timeSinceUpdate > fourHoursInMs;
  };

  // Fetch users list
  const fetchUsers = async () => {
    try {
      const data = await apiClient.getGroupSubscriptionStats({});
      const userArray = Object.entries(data.users).map(([email, details]) => ({
        ...details,
        email: email,
        isActive: details.isActive,
      }));
      setUsers(userArray.filter((u) => u.isActive)); // Only active users
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    }
  };

  // Load users on mount and check cache
  useEffect(() => {
    const loadData = async () => {
      await fetchUsers();
      const hasCachedData = getCachedData();
      if (hasCachedData) {
        toast.info('Loaded cached data');
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter usage data by current date range
  const filterDataByDateRange = (userData) => {
    const startTime = dateRange.startDate.getTime() / 1000; // Convert to Unix timestamp
    const endTime = dateRange.endDate.getTime() / 1000;

    return userData.filter((event) => {
      return event.time >= startTime && event.time <= endTime;
    });
  };

  // Helper function to calculate user metrics from raw usage data
  const calculateUserMetrics = (userData) => {
    // Filter by current date range for visits and hours (display metrics)
    const filteredData = filterDataByDateRange(userData);

    const uniqueConvoIds = new Set(filteredData.map((event) => event.convo_id).filter(Boolean));
    const visits = uniqueConvoIds.size;

    const totalSeconds = filteredData.reduce((sum, event) => sum + (event.details?.seconds || 0), 0);
    const hoursTranscribed = totalSeconds / 3600;

    // Last activity from ALL cached data (not date-range filtered)
    const lastEvent = userData.length > 0
      ? new Date(Math.max(...userData.map((e) => e.time * 1000))).toISOString()
      : null;

    // Rolling 30-day visits — used for adoption classification (always relative to today)
    const nowSec = Date.now() / 1000;
    const rollingVisits = new Set(
      userData.filter((e) => e.time >= nowSec - 30 * 86400).map((e) => e.convo_id).filter(Boolean),
    ).size;

    // WoW trend — this week vs previous week (unique visits)
    const thisWeek = new Set(
      userData.filter((e) => e.time >= nowSec - 7 * 86400).map((e) => e.convo_id).filter(Boolean),
    ).size;
    const lastWeek = new Set(
      userData.filter((e) => e.time >= nowSec - 14 * 86400 && e.time < nowSec - 7 * 86400)
        .map((e) => e.convo_id).filter(Boolean),
    ).size;
    const wowTrend = thisWeek > lastWeek * 1.1 ? 'up' : thisWeek < lastWeek * 0.9 ? 'down' : 'flat';

    return { visits, hoursTranscribed, lastEvent, totalEvents: filteredData.length, rollingVisits, wowTrend };
  };

  // Get all unique labels from all users
  const getAllUniqueLabels = () => {
    const labelSet = new Set();
    users.forEach((user) => {
      if (user.labels && Array.isArray(user.labels)) {
        user.labels.forEach((label) => labelSet.add(label));
      }
    });
    return Array.from(labelSet).sort();
  };

  // Group labels by prefix (text before the first "-"), e.g., LOCATION-*, DIVISION-*
  // Any group with only one label is merged into an "Other" catch-all bucket.
  const groupLabelsByPrefix = (labels) => {
    const groups = {};
    const catchAllKey = 'Other';
    const catchAll = [];

    labels.forEach((label) => {
      const prefix = (label.split('-')[0] || 'Other').trim() || 'Other';
      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      groups[prefix].push(label);
    });

    // Pull single-item groups into catch-all
    Object.entries(groups).forEach(([key, vals]) => {
      if (vals.length === 1) {
        catchAll.push(vals[0]);
        delete groups[key];
      }
    });

    // Merge any existing "Other" group
    if (groups[catchAllKey]) {
      catchAll.push(...groups[catchAllKey]);
      delete groups[catchAllKey];
    }

    if (catchAll.length > 0) {
      groups[catchAllKey] = catchAll.sort();
    }

    // Sort groups and labels for stable UI
    Object.keys(groups).forEach((key) => groups[key].sort());
    return Object.fromEntries(
      Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)),
    );
  };

  // Filter users by selected labels (OR logic - user must have at least one selected label)
  // and by the selected profession, so every view and the report honor both filters.
  const filterUsersByLabels = (allUsers) => {
    const userList = filterUsersByProfession(allUsers, selectedProfession);
    if (selectedLabels.length === 0) {
      return userList; // No filter applied
    }

    return userList.filter((user) => {
      if (!user.labels || user.labels.length === 0) {
        return false; // User has no labels
      }
      // Check if user has at least one of the selected labels
      return user.labels.some((label) => selectedLabels.includes(label));
    });
  };

  // Calculate statistics
  const calculateStats = () => {
    const adoptionCounts = {
      invited: 0,
      tryingItOut: 0,
      gettingTheHang: 0,
      powerUser: 0,
      potentiallyFailed: 0,
      churned: 0,
    };

    let totalHours = 0;
    let totalVisits = 0;

    // Apply label filter to users before calculating stats
    const filteredUsers = filterUsersByLabels(users);

    filteredUsers.forEach((user) => {
      const userData = usageData[user.email] || [];
      const metrics = calculateUserMetrics(userData);
      totalHours += metrics.hoursTranscribed;
      totalVisits += metrics.visits;

      const adoption = computeUserAdoption({
        credit_usage: metrics.rollingVisits,
        updated_at: metrics.lastEvent,
      });

      const label = adoption.label.toLowerCase();
      if (label === 'invited') adoptionCounts.invited++;
      else if (label === 'trying it out') adoptionCounts.tryingItOut++;
      else if (label === 'getting the hang of it')
        adoptionCounts.gettingTheHang++;
      else if (label === 'power user') adoptionCounts.powerUser++;
      else if (label === 'potentially failed onboarding')
        adoptionCounts.potentiallyFailed++;
      else if (label === 'churned') adoptionCounts.churned++;
    });

    const stats = {
      totalUsers: filteredUsers.length,
      usersWithData: Object.keys(usageData).filter(
        (email) => usageData[email]?.length > 0,
      ).length,
      totalEvents: Object.values(usageData).reduce(
        (sum, data) => sum + (data?.length || 0),
        0,
      ),
      totalVisits,
      totalHours,
      ...adoptionCounts,
    };
    return stats;
  };

  const stats = calculateStats();

  // Calculate statistics grouped by label
  const calculateLabelStats = () => {
    const labelStats = {};

    // Get labels to analyze (either selected labels or all labels)
    const labelsToAnalyze =
      selectedLabels.length > 0 ? selectedLabels : getAllUniqueLabels();

    labelsToAnalyze.forEach((label) => {
      // Find all users with this label
      const usersWithLabel = filterUsersByProfession(users, selectedProfession).filter(
        (user) => user.labels && user.labels.includes(label),
      );

      // Calculate aggregates for these users
      let totalVisits = 0;
      let totalHours = 0;
      const adoptionCounts = {
        invited: 0,
        tryingItOut: 0,
        gettingTheHang: 0,
        powerUser: 0,
        potentiallyFailed: 0,
        churned: 0,
      };

      usersWithLabel.forEach((user) => {
        const userData = usageData[user.email] || [];
        const metrics = calculateUserMetrics(userData);
        totalVisits += metrics.visits;
        totalHours += metrics.hoursTranscribed;

        const adoption = computeUserAdoption({
          credit_usage: metrics.visits,
          updated_at: metrics.lastEvent,
        });

        const adoptionLabel = adoption.label.toLowerCase();
        if (adoptionLabel === 'invited') adoptionCounts.invited++;
        else if (adoptionLabel === 'trying it out')
          adoptionCounts.tryingItOut++;
        else if (adoptionLabel === 'getting the hang of it')
          adoptionCounts.gettingTheHang++;
        else if (adoptionLabel === 'power user') adoptionCounts.powerUser++;
        else if (adoptionLabel === 'potentially failed onboarding')
          adoptionCounts.potentiallyFailed++;
        else if (adoptionLabel === 'churned') adoptionCounts.churned++;
      });

      labelStats[label] = {
        userCount: usersWithLabel.length,
        totalVisits,
        totalHours,
        avgVisits:
          usersWithLabel.length > 0
            ? (totalVisits / usersWithLabel.length).toFixed(1)
            : '0',
        avgHours:
          usersWithLabel.length > 0
            ? (totalHours / usersWithLabel.length).toFixed(1)
            : '0',
        ...adoptionCounts,
      };
    });

    return labelStats;
  };

  const labelStats = calculateLabelStats();

  // Calculate daily visit counts broken down by label or user
  const calculateDailyVisitsByEntity = () => {
    if (calendarViewMode === 'labels') {
      // Calculate visits per label per day
      const dailyVisitsByLabel = {};
      const labelsToShow =
        selectedLabels.length > 0 ? selectedLabels : getAllUniqueLabels();

      labelsToShow.forEach((label) => {
        const usersWithLabel = filterUsersByProfession(users, selectedProfession).filter(
          (user) => user.labels && user.labels.includes(label),
        );

        usersWithLabel.forEach((user) => {
          const userData = usageData[user.email] || [];

          userData.forEach((event) => {
            if (event.convo_id) {
              const eventDate = new Date(event.time * 1000);
              const dateKey = eventDate.toISOString().split('T')[0];

              if (!dailyVisitsByLabel[dateKey]) {
                dailyVisitsByLabel[dateKey] = {};
              }
              if (!dailyVisitsByLabel[dateKey][label]) {
                dailyVisitsByLabel[dateKey][label] = new Set();
              }
              dailyVisitsByLabel[dateKey][label].add(event.convo_id);
            }
          });
        });
      });

      // Convert Sets to counts
      const result = {};
      Object.keys(dailyVisitsByLabel).forEach((date) => {
        result[date] = {};
        Object.keys(dailyVisitsByLabel[date]).forEach((label) => {
          result[date][label] = dailyVisitsByLabel[date][label].size;
        });
      });

      return result;
    } else {
      // Calculate visits per user per day
      const dailyVisitsByUser = {};
      const filteredUsers = filterUsersByLabels(users);

      filteredUsers.forEach((user) => {
        const userData = usageData[user.email] || [];

        userData.forEach((event) => {
          if (event.convo_id) {
            const eventDate = new Date(event.time * 1000);
            const dateKey = eventDate.toISOString().split('T')[0];

            if (!dailyVisitsByUser[dateKey]) {
              dailyVisitsByUser[dateKey] = {};
            }
            if (!dailyVisitsByUser[dateKey][user.email]) {
              dailyVisitsByUser[dateKey][user.email] = new Set();
            }
            dailyVisitsByUser[dateKey][user.email].add(event.convo_id);
          }
        });
      });

      // Convert Sets to counts
      const result = {};
      Object.keys(dailyVisitsByUser).forEach((date) => {
        result[date] = {};
        Object.keys(dailyVisitsByUser[date]).forEach((userEmail) => {
          result[date][userEmail] = dailyVisitsByUser[date][userEmail].size;
        });
      });

      return result;
    }
  };

  const dailyVisitsByEntity = calculateDailyVisitsByEntity();

  // Get days in the current calendar month
  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

    const days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateKey = date.toISOString().split('T')[0];
      const entityVisits = dailyVisitsByEntity[dateKey] || {};
      const totalVisits = Object.values(entityVisits).reduce(
        (sum, count) => sum + count,
        0,
      );

      days.push({
        date: date,
        dateKey: dateKey,
        day: day,
        entityVisits: entityVisits, // Object with entity -> visit count
        totalVisits: totalVisits,
      });
    }

    return days;
  };

  const calendarDays = getCalendarDays();

  // Navigate calendar month
  const previousMonth = () => {
    const newMonth = new Date(calendarMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setCalendarMonth(newMonth);
  };

  const nextMonth = () => {
    const newMonth = new Date(calendarMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setCalendarMonth(newMonth);
  };

  const goToToday = () => {
    setCalendarMonth(new Date());
  };

  // Export calendar data to CSV
  const exportCalendarToCsv = () => {
    // Get all entities (labels or users) that appear in the data
    const allEntities = new Set();
    Object.values(dailyVisitsByEntity).forEach((dayData) => {
      Object.keys(dayData).forEach((entity) => allEntities.add(entity));
    });
    const entityList = Array.from(allEntities).sort();

    const headers = ['Date', 'Day of Week', 'Total Visits', ...entityList];
    let csvContent = headers.map((h) => `"${h}"`).join(',') + '\n';

    calendarDays
      .filter((dayObj) => dayObj !== null)
      .forEach((dayObj) => {
        const dayOfWeek = dayObj.date.toLocaleDateString('en-US', {
          weekday: 'long',
        });
        const row = [dayObj.dateKey, dayOfWeek, dayObj.totalVisits];

        // Add visit count for each entity
        entityList.forEach((entity) => {
          row.push(dayObj.entityVisits[entity] || 0);
        });

        csvContent +=
          row.map((val, idx) => (idx < 2 ? `"${val}"` : val)).join(',') + '\n';
      });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const monthName = calendarMonth
      .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      .replace(' ', '_');
    const viewType = calendarViewMode === 'labels' ? 'by_label' : 'by_user';
    link.setAttribute('href', url);
    link.setAttribute('download', `daily_visits_${viewType}_${monthName}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Calendar data exported to CSV');
  };

  // Handle CSV file upload for bulk labels
  const handleLabelsFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const sanitized = (text || '').replace(/^\uFEFF/, ''); // Remove BOM
        const lines = sanitized
          .split('\n')
          .map((l) => l.replace(/\r$/, ''))
          .filter((line) => line.trim());

        if (lines.length === 0) {
          setLabelsCsvData([]);
          toast.error('Empty CSV file');
          return;
        }

        // Detect delimiter: prefer comma, but support tab-delimited files
        const commaCols = lines[0].split(',').length;
        const tabCols = lines[0].split('\t').length;
        const delimiter = tabCols > commaCols ? '\t' : ',';

        // Parse header (case-insensitive, any order)
        const rawHeader = lines[0].split(delimiter).map((h) =>
          h
            .replace(/^\uFEFF/, '')
            .trim()
            .replace(/^"|"$/g, ''),
        );

        const headerMap = {};
        rawHeader.forEach((h, idx) => {
          const key = h.toLowerCase();
          if (key === 'email' || key === 'e-mail') {
            headerMap.email = idx;
          } else if (key === 'label' || key === 'labels') {
            headerMap.labels = idx;
          }
        });

        // Check if we have required columns
        if (headerMap.email === undefined || headerMap.labels === undefined) {
          toast.error('CSV must have "Email" and "Labels" columns');
          setLabelsCsvData([]);
          return;
        }

        // Parse data rows
        const parsedData = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i]
            .split(delimiter)
            .map((c) => c.trim().replace(/^"|"$/g, ''));

          const email = cols[headerMap.email]?.trim();
          const labelsStr = cols[headerMap.labels]?.trim();

          if (!email || !labelsStr) {
            continue; // Skip rows with missing data
          }

          // Split labels by semicolon and trim each
          const labels = labelsStr
            .split(';')
            .map((l) => l.trim())
            .filter((l) => l.length > 0);

          if (labels.length === 0) {
            continue; // Skip if no valid labels
          }

          parsedData.push({ email, labels });
        }

        if (parsedData.length === 0) {
          toast.error('No valid data found in CSV');
          setLabelsCsvData([]);
          return;
        }

        setLabelsCsvData(parsedData);
        toast.success(`Loaded ${parsedData.length} users from CSV`);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        toast.error('Error parsing CSV file');
        setLabelsCsvData([]);
      }
    };
    reader.readAsText(file);
  };

  // Handle bulk labels submission
  const handleBulkLabelsSubmit = async () => {
    if (labelsCsvData.length === 0) {
      toast.error('No data to submit. Please upload a CSV file first.');
      return;
    }

    setLabelsProcessing(true);

    try {
      // Call the backend API
      const response = await apiClient.bulkAddLabels({ users: labelsCsvData });

      if (response.result === 'success') {
        const { summary } = response;

        if (summary.errors === 0) {
          toast.success(
            `Successfully added labels to ${summary.success} user${summary.success !== 1 ? 's' : ''}`,
          );
          setShowLabelsModal(false);
          setLabelsCsvData([]);
        } else if (summary.success > 0) {
          toast.warning(
            `Partially completed: ${summary.success} succeeded, ${summary.errors} failed. Check console for details.`,
          );
          console.log('Bulk labels response:', response);
        } else {
          toast.error(
            `Failed to add labels to all users. Check console for details.`,
          );
          console.log('Bulk labels response:', response);
        }
      } else {
        toast.error(
          'Unexpected response from server. Check console for details.',
        );
        console.log('Bulk labels response:', response);
      }
    } catch (error) {
      console.error('Error adding bulk labels:', error);
      toast.error('Failed to add labels. Please try again.');
    } finally {
      setLabelsProcessing(false);
    }
  };

  // Download sample CSV with current user emails
  const handleDownloadSampleCsv = () => {
    if (users.length === 0) {
      toast.error('No users loaded. Please refresh data first.');
      return;
    }

    // Generate CSV content
    let csvContent = 'Email,Labels\n';

    users.forEach((user) => {
      // Add email and empty labels column
      csvContent += `${user.email},\n`;
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `user_labels_template_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Downloaded template with ${users.length} users`);
  };

  // Export Label Summary Table to CSV
  const exportLabelSummaryToCsv = () => {
    const headers = [
      'Label',
      'Users',
      'Total Visits',
      'Avg Visits',
      'Total Hours',
      'Avg Hours',
      'Power Users',
      'Getting Hang',
      'Trying Out',
      'Invited',
      'At Risk',
    ];

    let csvContent = headers.join(',') + '\n';

    Object.entries(labelStats)
      .sort((a, b) => b[1].userCount - a[1].userCount)
      .forEach(([label, stats]) => {
        const row = [
          `"${label}"`,
          stats.userCount,
          stats.totalVisits,
          stats.avgVisits,
          stats.totalHours.toFixed(1),
          stats.avgHours,
          stats.powerUser,
          stats.gettingTheHang,
          stats.tryingItOut,
          stats.invited,
          stats.potentiallyFailed + stats.churned,
        ];
        csvContent += row.join(',') + '\n';
      });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `label_summary_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Label summary exported to CSV');
  };

  // Export Adoption Breakdown to CSV
  const exportAdoptionBreakdownToCsv = () => {
    const headers = ['Adoption Stage', 'Count'];

    let csvContent = headers.join(',') + '\n';

    const rows = [
      ['Invited', stats.invited],
      ['Trying It Out', stats.tryingItOut],
      ['Getting the Hang of It', stats.gettingTheHang],
      ['Power User', stats.powerUser],
      ['Potentially Failed Onboarding', stats.potentiallyFailed],
      ['Churned', stats.churned],
      ['Total At Risk', stats.potentiallyFailed + stats.churned],
    ];

    rows.forEach((row) => {
      csvContent += `"${row[0]}",${row[1]}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `adoption_breakdown_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Adoption breakdown exported to CSV');
  };

  // Export Statistics Overview to CSV
  const exportStatsToCsv = () => {
    const headers = ['Metric', 'Value'];

    let csvContent = headers.join(',') + '\n';

    const rows = [
      ['Total Users', stats.totalUsers],
      ['Total Visits', stats.totalVisits],
      ['Total Events', stats.totalEvents],
      ['Total Hours Transcribed', (stats.totalHours || 0).toFixed(1)],
      ['Power Users', stats.powerUser],
      ['Getting the Hang of It', stats.gettingTheHang],
      ['Trying It Out', stats.tryingItOut],
      ['Invited', stats.invited],
      ['At Risk (Potentially Failed)', stats.potentiallyFailed],
      ['At Risk (Churned)', stats.churned],
      ['Total At Risk', stats.potentiallyFailed + stats.churned],
    ];

    rows.forEach((row) => {
      csvContent += `"${row[0]}",${row[1]}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `adoption_statistics_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Statistics exported to CSV');
  };

  // Export User Details Table to CSV
  const exportUserDetailsToCsv = () => {
    const headers = [
      'Name',
      'Email',
      'Labels',
      'Visits',
      'Hours Transcribed',
      'Last Activity',
      'Days Since Last Use',
      'Adoption Stage',
    ];

    let csvContent = headers.join(',') + '\n';

    const filteredUsers = filterUsersByLabels(users);

    filteredUsers
      .map((user) => {
        const userData = usageData[user.email] || [];
        const metrics = calculateUserMetrics(userData);

        const adoption = computeUserAdoption({
          credit_usage: metrics.visits,
          updated_at: metrics.lastEvent,
        });

        const lastEventDate = metrics.lastEvent
          ? normalizeTimestampToDate(metrics.lastEvent)
          : null;
        const daysSince = lastEventDate
          ? daysDiffFromToday(lastEventDate)
          : null;

        return {
          ...user,
          ...metrics,
          adoption,
          daysSince,
        };
      })
      .sort((a, b) => {
        const priority = {
          'Power User': 1,
          'Getting the Hang of It': 2,
          'Trying It Out': 3,
          Invited: 4,
          'Potentially Failed Onboarding': 5,
          Churned: 6,
        };
        const aPriority = priority[a.adoption.label] || 99;
        const bPriority = priority[b.adoption.label] || 99;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return b.visits - a.visits;
      })
      .forEach((user) => {
        const row = [
          `"${user.name || ''}"`,
          `"${user.email}"`,
          `"${user.labels && user.labels.length > 0 ? user.labels.join('; ') : ''}"`,
          user.visits,
          user.hoursTranscribed.toFixed(2),
          `"${user.lastEvent ? formatLastUsedPretty(user.lastEvent) : 'Never'}"`,
          user.daysSince !== null ? user.daysSince : '',
          `"${user.adoption.label}"`,
        ];
        csvContent += row.join(',') + '\n';
      });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `user_activity_details_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('User details exported to CSV');
  };

  // ── Chart helpers ──────────────────────────────────────────────────

  // Returns the "period key" (string) for a unix-seconds timestamp
  const getPeriodKey = (unixSeconds, period) => {
    const d = new Date(unixSeconds * 1000);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    if (period === 'daily') return `${year}-${month}-${day}`;
    if (period === 'monthly') return `${year}-${month}`;
    // weekly – key is the Monday of the week
    const tmp = new Date(d);
    const dow = tmp.getDay(); // 0=Sun
    const diff = dow === 0 ? -6 : 1 - dow;
    tmp.setDate(tmp.getDate() + diff);
    tmp.setHours(0, 0, 0, 0);
    const wy = tmp.getFullYear();
    const wm = String(tmp.getMonth() + 1).padStart(2, '0');
    const wd = String(tmp.getDate()).padStart(2, '0');
    return `${wy}-${wm}-${wd}`;
  };

  // Generate ordered list of period keys covering startDate..endDate
  const generatePeriods = (startDate, endDate, period) => {
    const periods = [];
    const seen = new Set();
    const current = new Date(startDate);
    current.setHours(12, 0, 0, 0);
    while (current <= endDate) {
      const key = getPeriodKey(current.getTime() / 1000, period);
      if (!seen.has(key)) { seen.add(key); periods.push(key); }
      if (period === 'daily') current.setDate(current.getDate() + 1);
      else if (period === 'weekly') current.setDate(current.getDate() + 7);
      else current.setMonth(current.getMonth() + 1);
    }
    return periods;
  };

  // Human-readable x-axis label
  const formatPeriodLabel = (key, period) => {
    if (period === 'monthly') {
      const [y, m] = key.split('-');
      const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${names[parseInt(m, 10) - 1]} ${y}`;
    }
    const [, m, d] = key.split('-');
    return `${m}/${d}`;
  };

  // Unix-seconds for the END of a period key
  const getPeriodEndTime = (key, period) => {
    if (period === 'daily') {
      const [y, m, d] = key.split('-').map(Number);
      return new Date(y, m - 1, d, 23, 59, 59).getTime() / 1000;
    }
    if (period === 'monthly') {
      const [y, m] = key.split('-').map(Number);
      const lastDay = new Date(y, m, 0).getDate();
      return new Date(y, m - 1, lastDay, 23, 59, 59).getTime() / 1000;
    }
    // weekly – key is Monday; end is Sunday 23:59:59
    const [y, m, d] = key.split('-').map(Number);
    const monday = new Date(y, m - 1, d);
    monday.setDate(monday.getDate() + 6);
    monday.setHours(23, 59, 59);
    return monday.getTime() / 1000;
  };

  // Compute adoption category at a given reference date using a rolling 30-day visit window.
  // visits = unique convo_ids in the 30 days ending at referenceEndTime (not all-time).
  // diffDays = days since last event relative to referenceEndTime.
  // Thresholds: ≤10 rolling/30d → Trying It Out; 11-30 → Getting the Hang; 31+ → Power User.
  const computeAdoptionAtDate = (eventsUpTo, referenceEndTime) => {
    if (!eventsUpTo || eventsUpTo.length === 0) return 'invited';

    const lastEventTime = Math.max(...eventsUpTo.map((e) => e.time));
    const lastDt = new Date(lastEventTime * 1000);
    const refDate = new Date(referenceEndTime * 1000);
    const diffDays = Math.floor(
      (new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate()) -
       new Date(lastDt.getFullYear(), lastDt.getMonth(), lastDt.getDate())) /
      (24 * 60 * 60 * 1000),
    );

    if (diffDays > 14) return 'atRisk';

    // Rolling 30-day unique visits (the classification signal)
    const thirtyDaysAgo = referenceEndTime - 30 * 86400;
    const rollingVisits = new Set(
      eventsUpTo.filter((e) => e.time >= thirtyDaysAgo).map((e) => e.convo_id).filter(Boolean),
    ).size;

    if (rollingVisits === 0) return 'invited';
    if (rollingVisits <= 10 && diffDays <= 5) return 'tryingItOut';
    if (rollingVisits > 10 && rollingVisits <= 30 && diffDays <= 7) return 'gettingTheHang';
    if (rollingVisits > 30 && diffDays <= 14) return 'powerUser';
    if (diffDays > 7) return 'atRisk';
    return 'tryingItOut';
  };

  // Chart 1 – Cumulative Users Onboarded (line)
  const computeOnboardingChartData = () => {
    const filteredUsers = filterUsersByLabels(users);
    const periods = generatePeriods(dateRange.startDate, dateRange.endDate, chartPeriod);
    const newPerPeriod = {};
    periods.forEach((p) => (newPerPeriod[p] = 0));

    filteredUsers.forEach((user) => {
      const userData = usageData[user.email] || [];
      if (userData.length === 0) return;
      const firstTime = Math.min(...userData.map((e) => e.time));
      const key = getPeriodKey(firstTime, chartPeriod);
      if (newPerPeriod[key] !== undefined) newPerPeriod[key]++;
    });

    let cumulative = 0;
    const data = periods.map((p) => {
      cumulative += newPerPeriod[p];
      return cumulative;
    });

    return {
      labels: periods.map((p) => formatPeriodLabel(p, chartPeriod)),
      datasets: [
        {
          label: 'Total Users Onboarded',
          data,
          borderColor: '#4e73df',
          backgroundColor: 'rgba(78,115,223,0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 3,
        },
      ],
    };
  };

  // Chart 2 – Visits & Hours Transcribed over time (dual-axis bar)
  const computeUsageOverTimeChartData = () => {
    const filteredUsers = filterUsersByLabels(users);
    const periods = generatePeriods(dateRange.startDate, dateRange.endDate, chartPeriod);
    const visitSets = {};
    const secondsByPeriod = {};
    periods.forEach((p) => { visitSets[p] = new Set(); secondsByPeriod[p] = 0; });

    filteredUsers.forEach((user) => {
      (usageData[user.email] || []).forEach((event) => {
        const key = getPeriodKey(event.time, chartPeriod);
        if (visitSets[key] !== undefined) {
          if (event.convo_id) visitSets[key].add(`${user.email}_${event.convo_id}`);
          secondsByPeriod[key] += event.details?.seconds || 0;
        }
      });
    });

    return {
      labels: periods.map((p) => formatPeriodLabel(p, chartPeriod)),
      datasets: [
        {
          label: 'Visits',
          data: periods.map((p) => visitSets[p].size),
          backgroundColor: 'rgba(78,115,223,0.8)',
          yAxisID: 'y-visits',
        },
        {
          label: 'Hours Transcribed',
          data: periods.map((p) => parseFloat((secondsByPeriod[p] / 3600).toFixed(1))),
          backgroundColor: 'rgba(231,74,59,0.8)',
          yAxisID: 'y-hours',
        },
      ],
    };
  };

  // Chart 3 – Adoption Status stacked bar (snapshot at end of each period)
  const computeAdoptionStatusChartData = () => {
    const filteredUsers = filterUsersByLabels(users);
    const periods = generatePeriods(dateRange.startDate, dateRange.endDate, chartPeriod);

    const counts = { powerUser: {}, gettingTheHang: {}, tryingItOut: {}, invited: {}, atRisk: {} };
    periods.forEach((p) => {
      Object.keys(counts).forEach((k) => (counts[k][p] = 0));
    });

    filteredUsers.forEach((user) => {
      const userData = usageData[user.email] || [];
      periods.forEach((p) => {
        const endTime = getPeriodEndTime(p, chartPeriod);
        const eventsUpTo = userData.filter((e) => e.time <= endTime);
        if (eventsUpTo.length === 0) return;
        const category = computeAdoptionAtDate(eventsUpTo, endTime);
        if (counts[category]) counts[category][p]++;
        else counts.atRisk[p]++;
      });
    });

    return {
      labels: periods.map((p) => formatPeriodLabel(p, chartPeriod)),
      datasets: [
        {
          label: 'Power Users',
          data: periods.map((p) => counts.powerUser[p]),
          backgroundColor: 'rgba(28,200,138,0.85)',
          stack: 'adoption',
        },
        {
          label: 'Getting the Hang of It',
          data: periods.map((p) => counts.gettingTheHang[p]),
          backgroundColor: 'rgba(54,185,204,0.85)',
          stack: 'adoption',
        },
        {
          label: 'Trying It Out',
          data: periods.map((p) => counts.tryingItOut[p]),
          backgroundColor: 'rgba(246,194,62,0.85)',
          stack: 'adoption',
        },
        {
          label: 'At Risk',
          data: periods.map((p) => counts.atRisk[p]),
          backgroundColor: 'rgba(231,74,59,0.85)',
          stack: 'adoption',
        },
      ],
    };
  };

  // Chart 4 – Active Users Per Period (distinct users who had at least 1 event)
  const computeActiveUsersChartData = () => {
    const filteredUsers = filterUsersByLabels(users);
    const periods = generatePeriods(dateRange.startDate, dateRange.endDate, chartPeriod);
    const activeByPeriod = {};
    periods.forEach((p) => (activeByPeriod[p] = new Set()));
    filteredUsers.forEach((user) => {
      (usageData[user.email] || []).forEach((event) => {
        const key = getPeriodKey(event.time, chartPeriod);
        if (activeByPeriod[key] !== undefined) activeByPeriod[key].add(user.email);
      });
    });
    return {
      labels: periods.map((p) => formatPeriodLabel(p, chartPeriod)),
      datasets: [{
        label: 'Active Users',
        data: periods.map((p) => activeByPeriod[p].size),
        borderColor: '#1cc88a',
        backgroundColor: 'rgba(28,200,138,0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
      }],
    };
  };

  // Chart 5 – New vs Returning Users per period
  const computeNewVsReturningChartData = () => {
    const filteredUsers = filterUsersByLabels(users);
    const periods = generatePeriods(dateRange.startDate, dateRange.endDate, chartPeriod);
    const firstPeriodByUser = {};
    filteredUsers.forEach((user) => {
      const ud = usageData[user.email] || [];
      if (!ud.length) return;
      firstPeriodByUser[user.email] = getPeriodKey(Math.min(...ud.map((e) => e.time)), chartPeriod);
    });
    const newCounts = {};
    const returningCounts = {};
    periods.forEach((p) => { newCounts[p] = 0; returningCounts[p] = 0; });
    filteredUsers.forEach((user) => {
      const activePeriods = new Set((usageData[user.email] || []).map((e) => getPeriodKey(e.time, chartPeriod)));
      activePeriods.forEach((p) => {
        if (!periods.includes(p)) return;
        if (firstPeriodByUser[user.email] === p) newCounts[p]++;
        else returningCounts[p]++;
      });
    });
    return {
      labels: periods.map((p) => formatPeriodLabel(p, chartPeriod)),
      datasets: [
        { label: 'New Users', data: periods.map((p) => newCounts[p]), backgroundColor: 'rgba(78,115,223,0.8)', stack: 'users' },
        { label: 'Returning Users', data: periods.map((p) => returningCounts[p]), backgroundColor: 'rgba(28,200,138,0.8)', stack: 'users' },
      ],
    };
  };

  // Chart 6 – Avg Visits/User & Avg Hrs/User over time (all users, unfiltered)
  const computeAvgPerUserChartData = () => {
    const periods = generatePeriods(dateRange.startDate, dateRange.endDate, chartPeriod);

    // Per period: count active users, total visits, total hours — across ALL users (no label filter)
    const visitsByPeriod = {};
    const secondsByPeriod = {};
    const activeUsersByPeriod = {};
    periods.forEach((p) => {
      visitsByPeriod[p] = new Set();   // unique convo_ids
      secondsByPeriod[p] = 0;
      activeUsersByPeriod[p] = new Set(); // active user emails
    });

    users.forEach((user) => {
      (usageData[user.email] || []).forEach((event) => {
        const key = getPeriodKey(event.time, chartPeriod);
        if (visitsByPeriod[key] !== undefined) {
          if (event.convo_id) visitsByPeriod[key].add(event.convo_id);
          secondsByPeriod[key] += event.details?.seconds || 0;
          activeUsersByPeriod[key].add(user.email);
        }
      });
    });

    return {
      labels: periods.map((p) => formatPeriodLabel(p, chartPeriod)),
      datasets: [
        {
          label: 'Avg Visits / User',
          data: periods.map((p) => {
            const activeCount = activeUsersByPeriod[p].size;
            return activeCount > 0 ? parseFloat((visitsByPeriod[p].size / activeCount).toFixed(2)) : 0;
          }),
          borderColor: '#4e73df',
          backgroundColor: 'rgba(78,115,223,0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          yAxisID: 'visits',
        },
        {
          label: 'Avg Hrs / User',
          data: periods.map((p) => {
            const activeCount = activeUsersByPeriod[p].size;
            return activeCount > 0 ? parseFloat((secondsByPeriod[p] / 3600 / activeCount).toFixed(2)) : 0;
          }),
          borderColor: '#1cc88a',
          backgroundColor: 'rgba(28,200,138,0.1)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          yAxisID: 'hrs',
        },
      ],
    };
  };

  // Chart 7 – Usage Bubble Heatmap: date (x) × hour-of-day (y), bubble size = unique visits in that 1-hr window
  const computeUsageHeatmapData = () => {
    const filteredUsers = filterUsersByLabels(users);
    const startTime = dateRange.startDate.getTime() / 1000;
    const endTime = dateRange.endDate.getTime() / 1000;

    // Bucket events into (date, hour) windows and collect unique convo_ids
    const buckets = {}; // "YYYY-MM-DD_HH" → { dateMs, hour, visits: Set }
    filteredUsers.forEach((user) => {
      (usageData[user.email] || [])
        .filter((e) => e.time >= startTime && e.time <= endTime)
        .forEach((event) => {
          const d = new Date(event.time * 1000);
          // Use LOCAL date + LOCAL hour so both axes agree. Mixing a UTC date
          // (toISOString) with a local hour (getHours) shifts late-night events
          // onto the next day's column, producing bubbles in "future" hours.
          const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const hour = d.getHours();
          const key = `${dateStr}_${hour}`;
          if (!buckets[key]) {
            buckets[key] = { dateMs: new Date(dateStr + 'T00:00:00').getTime(), hour, visits: new Set() };
          }
          if (event.convo_id) buckets[key].visits.add(event.convo_id);
        });
    });

    const raw = Object.values(buckets).map((b) => ({ dateMs: b.dateMs, hour: b.hour, count: b.visits.size }));
    const maxCount = Math.max(...raw.map((p) => p.count), 1);
    const minThreshold = hideHeatmapNoise ? Math.ceil(maxCount * 0.1) : 0;
    const visible = minThreshold > 0 ? raw.filter((p) => p.count >= minThreshold) : raw;

    return {
      datasets: [{
        label: 'Unique Visits',
        data: visible.map((p) => ({
          x: p.dateMs,
          y: 23 - p.hour,          // invert: 0 (midnight) → top (23), 23 (11PM) → bottom (0)
          r: Math.max(3, Math.round((p.count / maxCount) * 18) + 2),
          _count: p.count, // stored for tooltip
          _hour: p.hour,   // original hour for tooltip
        })),
        backgroundColor: 'rgba(78,115,223,0.5)',
        borderColor: 'rgba(78,115,223,0.8)',
        borderWidth: 1,
      }],
    };
  };

  const hasChartData = Object.keys(usageData).length > 0;
  const uniqueProfessions = getUniqueProfessions(users);
  const professionOptions = getProfessionFilterOptions(uniqueProfessions);

  return (
    <div style={{ padding: '0 20px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <div>
          <h3>Adoption Dashboard</h3>
          {lastUpdated && (
            <small style={{ color: '#6c757d' }}>
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </small>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            color="primary"
            size="sm"
            onClick={generateReport}
            disabled={loading || users.length === 0}
          >
            {loading ? 'Starting...' : 'Refresh Data'}
          </Button>
          <Button
            color="secondary"
            size="sm"
            onClick={clearCache}
            disabled={loading}
          >
            Clear Cache
          </Button>
          <Button
            color="warning"
            size="sm"
            onClick={() => importFileRef.current?.click()}
            disabled={loading}
          >
            Import .cpta
          </Button>
          <input
            ref={importFileRef}
            type="file"
            accept=".cpta"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
          <Button
            color="info"
            size="sm"
            onClick={() => setShowLabelsModal(true)}
            disabled={loading}
          >
            Add Labels
          </Button>
        </div>
      </div>

      {reportFailedUsers.length > 0 && (
        <div className="alert alert-warning" role="alert">
          Usage for {reportFailedUsers.length} user
          {reportFailedUsers.length === 1 ? '' : 's'} could not be loaded in this
          report, so they show no activity:{' '}
          {reportFailedUsers.map((u) => u.email).join(', ')}
        </div>
      )}

      <AdoptionReports
        apiClient={apiClient}
        reloadKey={reportsReloadKey}
        onViewReport={loadCptaPayload}
      />

      {/* Date Range & Filters */}
      <Card style={{ marginBottom: '20px' }}>
        <CardHeader
          onClick={() => setCollapseFilters(!collapseFilters)}
          style={{
            cursor: 'pointer',
            backgroundColor: '#f7fafc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h4 style={{ margin: 0 }}>📅 Date Range & Filters</h4>
          <i className={`fa fa-chevron-${collapseFilters ? 'up' : 'down'}`}></i>
        </CardHeader>
        <Collapse isOpen={collapseFilters}>
          <div style={{ padding: '20px' }}>
            <FormGroup row>
              <Label sm={2}>Date Range:</Label>
              <Col sm={10}>
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <DatePicker
                    selected={dateRange.startDate}
                    onChange={(date) =>
                      setDateRange({ ...dateRange, startDate: date })
                    }
                    selectsStart
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    maxDate={dateRange.endDate}
                    dateFormat="MMM d, yyyy"
                    className="form-control"
                    disabled={loading}
                  />
                  <span>to</span>
                  <DatePicker
                    selected={dateRange.endDate}
                    onChange={(date) =>
                      setDateRange({ ...dateRange, endDate: date })
                    }
                    selectsEnd
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    minDate={dateRange.startDate}
                    maxDate={new Date()}
                    dateFormat="MMM d, yyyy"
                    className="form-control"
                    disabled={loading}
                  />
                  <Button
                    color="primary"
                    size="sm"
                    onClick={generateReport}
                    disabled={loading || users.length === 0}
                  >
                    {loading ? 'Starting...' : 'Generate Report'}
                  </Button>
                  {cachedDateRange &&
                    !needsNewData(dateRange) &&
                    !isDataStale() && (
                      <Badge color="success" pill>
                        Using cached data
                      </Badge>
                    )}
                  {cachedDateRange &&
                    (needsNewData(dateRange) || isDataStale()) && (
                      <Badge color="warning" pill>
                        New data needed
                      </Badge>
                    )}
                </div>
                {lastUpdated && cachedDateRange && (
                  <small
                    style={{
                      color: '#6c757d',
                      marginTop: '5px',
                      display: 'block',
                    }}
                  >
                    Cached range:{' '}
                    {cachedDateRange.startDate.toLocaleDateString()} -{' '}
                    {cachedDateRange.endDate.toLocaleDateString()}
                  </small>
                )}
              </Col>
            </FormGroup>

            {/* Label Filter */}
            <FormGroup row style={{ marginTop: '15px' }}>
              <Label sm={2}>Filter by Labels:</Label>
              <Col sm={10}>
                {getAllUniqueLabels().length > 0 ? (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap',
                        alignItems: 'stretch',
                        padding: '8px',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        background: '#f8f9fa',
                      }}
                    >
                      {Object.entries(
                        groupLabelsByPrefix(getAllUniqueLabels()),
                      )
                        .filter(([, labels]) => labels.length > 0)
                        .map(([prefix, labels]) => {
                          const selectedForGroup = selectedLabels.filter((l) =>
                            labels.includes(l),
                          );
                          const size = Math.min(
                            Math.max(4, labels.length),
                            10,
                          );
                          const clearGroup = () => {
                            const remaining = selectedLabels.filter(
                              (l) => !labels.includes(l),
                            );
                            setSelectedLabels(remaining);
                          };
                          return (
                            <div
                              key={prefix}
                              style={{
                                minWidth: '200px',
                                background: '#fff',
                                border: '1px solid #e1e7ed',
                                borderRadius: '6px',
                                padding: '8px',
                                boxShadow:
                                  '0 1px 2px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.02)',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  marginBottom: '6px',
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                    textTransform: 'uppercase',
                                    color: '#495057',
                                  }}
                                >
                                  {prefix}
                                </div>
                                <Badge color="light" style={{ color: '#495057' }}>
                                  {selectedForGroup.length}/{labels.length}
                                </Badge>
                              </div>
                              <Input
                                type="select"
                                multiple
                                value={selectedForGroup}
                                onChange={(e) => {
                                  const selected = Array.from(
                                    e.target.selectedOptions,
                                    (opt) => opt.value,
                                  );
                                  const others = selectedLabels.filter(
                                    (l) => !labels.includes(l),
                                  );
                                  setSelectedLabels([
                                    ...new Set([...others, ...selected]),
                                  ]);
                                }}
                                size={size}
                                style={{
                                  fontSize: '13px',
                                  borderRadius: '4px',
                                  borderColor: '#d4dce4',
                                  boxShadow: 'none',
                                }}
                              >
                                {labels.map((label) => (
                                  <option key={label} value={label}>
                                    {label}
                                  </option>
                                ))}
                              </Input>
                              {selectedForGroup.length > 0 && (
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    marginTop: '6px',
                                  }}
                                >
                                  <Button color="link" size="sm" onClick={clearGroup}>
                                    Clear {prefix}
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    <div style={{ marginTop: '6px' }}>
                      {selectedLabels.length > 0 && (
                        <Button
                          color="link"
                          size="sm"
                          onClick={() => setSelectedLabels([])}
                          style={{ padding: '2px 8px', fontSize: '12px' }}
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                    {selectedLabels.length > 0 && (
                      <div
                        style={{
                          marginTop: '4px',
                          display: 'flex',
                          gap: '6px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {selectedLabels.map((label) => (
                          <Badge
                            key={label}
                            color="primary"
                            pill
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              setSelectedLabels(
                                selectedLabels.filter((l) => l !== label),
                              )
                            }
                          >
                            {label} ✕
                          </Badge>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <small style={{ color: '#6c757d' }}>
                    No labels available. Add labels using the "Add Labels"
                    button.
                  </small>
                )}
                {selectedLabels.length > 0 && (
                  <small
                    style={{
                      color: '#6c757d',
                      marginTop: '5px',
                      display: 'block',
                    }}
                  >
                    Showing users with: {selectedLabels.join(', ')}
                  </small>
                )}
              </Col>
            </FormGroup>

            {/* Profession Filter */}
            <FormGroup row style={{ marginTop: '15px' }}>
              <Label sm={2} for="adoption-profession-filter">
                Filter by Profession:
              </Label>
              <Col sm={4}>
                <Input
                  id="adoption-profession-filter"
                  type="select"
                  value={selectedProfession}
                  onChange={(e) => setSelectedProfession(e.target.value)}
                  disabled={uniqueProfessions.length === 0}
                >
                  <option value="">All professions</option>
                  {professionOptions.map((profession) => (
                    <option key={profession} value={profession}>
                      {profession}
                    </option>
                  ))}
                </Input>
              </Col>
              {selectedProfession && (
                <Col sm={2} style={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    color="link"
                    size="sm"
                    onClick={() => setSelectedProfession('')}
                    style={{ padding: '2px 8px', fontSize: '12px' }}
                  >
                    Clear
                  </Button>
                </Col>
              )}
            </FormGroup>
          </div>
        </Collapse>
      </Card>

      {/* Stats Cards */}
      <Card style={{ marginBottom: '20px' }}>
        <CardHeader
          onClick={() => setCollapseStats(!collapseStats)}
          style={{
            cursor: 'pointer',
            backgroundColor: '#f7fafc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h4 style={{ margin: 0 }}>📊 Statistics Overview</h4>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Button
              color="success"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                exportStatsToCsv();
              }}
            >
              <i className="fa fa-download" style={{ marginRight: '5px' }}></i>
              Export CSV
            </Button>
            <i className={`fa fa-chevron-${collapseStats ? 'up' : 'down'}`}></i>
          </div>
        </CardHeader>
        <Collapse isOpen={collapseStats}>
          <div style={{ padding: '20px' }}>
            <Row>
              <div className="col-md-3">
                <Card body>
                  <h5>Total Users</h5>
                  <h2>{stats.totalUsers}</h2>
                </Card>
              </div>
              <div className="col-md-3">
                <Card body>
                  <h5>Total Visits</h5>
                  <h2>{stats.totalVisits}</h2>
                  <small className="text-muted">
                    {stats.totalEvents} events
                  </small>
                </Card>
              </div>
              <div className="col-md-3">
                <Card body>
                  <h5>Hours Transcribed</h5>
                  <h2>{(stats.totalHours || 0).toFixed(1)}</h2>
                </Card>
              </div>
              <div className="col-md-3">
                <Card body style={{ borderLeft: '3px solid #11cdef' }}>
                  <h6>Power Users</h6>
                  <h3>{stats.powerUser}</h3>
                  <small className="text-muted" style={{ color: '#f5365c' }}>
                    At Risk: {stats.potentiallyFailed + stats.churned}
                  </small>
                </Card>
              </div>
            </Row>
          </div>
        </Collapse>
      </Card>

      {/* Charts Section */}
      {hasChartData && (() => {
        const currentPeriods = generatePeriods(dateRange.startDate, dateRange.endDate, chartPeriod);

        // Click handler: drills into a data point and opens the detail panel
        const getClickHandler = (chartKey) => (event, elements) => {
          if (!elements || elements.length === 0) return;
          const idx = elements[0]._index;
          const datasetIdx = elements[0]._datasetIndex;
          const filteredUsers = filterUsersByLabels(users);

          if (chartKey === 'providerAvgs') {
            const period = currentPeriods[idx];
            if (!period) { if (!fullscreenChart) setFullscreenChart(chartKey); return; }
            const endTime = getPeriodEndTime(period, chartPeriod);
            const prevPeriod = idx > 0 ? currentPeriods[idx - 1] : null;
            const startTime = prevPeriod ? getPeriodEndTime(prevPeriod, chartPeriod) : dateRange.startDate.getTime() / 1000;
            const periodLabel = formatPeriodLabel(period, chartPeriod);
            // Drill down: all users active in this period with their visits + hrs
            const rows = users
              .map((user) => {
                const events = (usageData[user.email] || []).filter((e) => e.time > startTime && e.time <= endTime);
                const visits = new Set(events.map((e) => e.convo_id).filter(Boolean)).size;
                const hrs = parseFloat((events.reduce((s, e) => s + (e.details?.seconds || 0), 0) / 3600).toFixed(2));
                return { Name: user.name || '—', Email: user.email, Visits: visits, Hrs: hrs };
              })
              .filter((r) => r.Visits > 0)
              .sort((a, b) => b.Visits - a.Visits);
            setChartClickDetail({ title: `All Users – ${periodLabel}`, rows, columns: ['Name', 'Email', 'Visits', 'Hrs'] });
            if (!fullscreenChart) setFullscreenChart(chartKey);
            return;
          }

          if (chartKey === 'usageHeatmap') {
            if (!fullscreenChart) setFullscreenChart(chartKey);
            return;
          }

          const period = currentPeriods[idx];
          if (!period) return;
          const periodLabel = formatPeriodLabel(period, chartPeriod);

          if (chartKey === 'onboarding') {
            const rows = filteredUsers
              .filter((user) => {
                const ud = usageData[user.email] || [];
                if (!ud.length) return false;
                return getPeriodKey(Math.min(...ud.map((e) => e.time)), chartPeriod) === period;
              })
              .map((user) => ({
                Name: user.name || '—',
                Email: user.email,
                Labels: (user.labels || []).join(', ') || '—',
              }));
            setChartClickDetail({ title: `Newly Onboarded – ${periodLabel}`, rows, columns: ['Name', 'Email', 'Labels'] });

          } else if (chartKey === 'usage') {
            const endTime = getPeriodEndTime(period, chartPeriod);
            const prevPeriod = idx > 0 ? currentPeriods[idx - 1] : null;
            const startTime = prevPeriod ? getPeriodEndTime(prevPeriod, chartPeriod) : dateRange.startDate.getTime() / 1000;
            const rows = filteredUsers
              .map((user) => {
                const events = (usageData[user.email] || []).filter((e) => e.time > startTime && e.time <= endTime);
                const visits = new Set(events.map((e) => e.convo_id).filter(Boolean)).size;
                const hours = (events.reduce((s, e) => s + (e.details?.seconds || 0), 0) / 3600).toFixed(1);
                return { Name: user.name || '—', Email: user.email, Visits: visits, Hours: hours };
              })
              .filter((r) => r.Visits > 0)
              .sort((a, b) => b.Visits - a.Visits);
            setChartClickDetail({ title: `Active Users – ${periodLabel}`, rows, columns: ['Name', 'Email', 'Visits', 'Hours'] });

          } else if (chartKey === 'adoption') {
            // Order must match computeAdoptionStatusChartData datasets: Power Users, Getting the Hang, Trying It Out, At Risk
            const catKeys = ['powerUser', 'gettingTheHang', 'tryingItOut', 'atRisk'];
            const catNames = ['Power Users', 'Getting the Hang of It', 'Trying It Out', 'At Risk'];

            // chart.js v2 stacked bar onClick often returns the wrong _datasetIndex (nearest-bar logic
            // picks the bottom/tallest segment). Resolve the actual segment using y-coordinate instead.
            let resolvedDatasetIdx = datasetIdx;
            if (elements.length > 1) {
              const clickY = event.offsetY;
              for (const el of elements) {
                const m = el._model;
                if (m) {
                  const top = Math.min(m.y, m.base);
                  const bottom = Math.max(m.y, m.base);
                  if (clickY >= top && clickY <= bottom) {
                    resolvedDatasetIdx = el._datasetIndex;
                    break;
                  }
                }
              }
            }

            const category = catKeys[resolvedDatasetIdx];
            const categoryName = catNames[resolvedDatasetIdx] || 'Unknown';
            const endTime = getPeriodEndTime(period, chartPeriod);
            const rows = filteredUsers
              .filter((user) => {
                const eventsUpTo = (usageData[user.email] || []).filter((e) => e.time <= endTime);
                if (!eventsUpTo.length) return category === 'invited';
                return computeAdoptionAtDate(eventsUpTo, endTime) === category;
              })
              .map((user) => {
                const eventsUpTo = (usageData[user.email] || []).filter((e) => e.time <= endTime);
                const visits = new Set(eventsUpTo.map((e) => e.convo_id).filter(Boolean)).size;
                return { Name: user.name || '—', Email: user.email, Visits: visits };
              })
              .sort((a, b) => b.Visits - a.Visits);
            setChartClickDetail({ title: `${categoryName} – ${periodLabel}`, rows, columns: ['Name', 'Email', 'Visits'] });

          } else if (chartKey === 'active') {
            const endTime = getPeriodEndTime(period, chartPeriod);
            const prevPeriod = idx > 0 ? currentPeriods[idx - 1] : null;
            const startTime = prevPeriod ? getPeriodEndTime(prevPeriod, chartPeriod) : dateRange.startDate.getTime() / 1000;
            const rows = filteredUsers
              .map((user) => {
                const events = (usageData[user.email] || []).filter((e) => e.time > startTime && e.time <= endTime);
                const visits = new Set(events.map((e) => e.convo_id).filter(Boolean)).size;
                const hours = (events.reduce((s, e) => s + (e.details?.seconds || 0), 0) / 3600).toFixed(1);
                return { Name: user.name || '—', Email: user.email, Visits: visits, Hours: hours };
              })
              .filter((r) => r.Visits > 0)
              .sort((a, b) => b.Visits - a.Visits);
            setChartClickDetail({ title: `Active Users – ${periodLabel}`, rows, columns: ['Name', 'Email', 'Visits', 'Hours'] });

          } else if (chartKey === 'newVsReturning') {
            const firstPeriodByUser = {};
            filteredUsers.forEach((user) => {
              const ud = usageData[user.email] || [];
              if (!ud.length) return;
              firstPeriodByUser[user.email] = getPeriodKey(Math.min(...ud.map((e) => e.time)), chartPeriod);
            });
            const endTime = getPeriodEndTime(period, chartPeriod);
            const prevPeriod = idx > 0 ? currentPeriods[idx - 1] : null;
            const startTime = prevPeriod ? getPeriodEndTime(prevPeriod, chartPeriod) : dateRange.startDate.getTime() / 1000;
            const isNewDataset = datasetIdx === 0;
            const rows = filteredUsers
              .filter((user) => {
                const events = (usageData[user.email] || []).filter((e) => e.time > startTime && e.time <= endTime);
                if (!events.length) return false;
                const isNew = firstPeriodByUser[user.email] === period;
                return isNewDataset ? isNew : !isNew;
              })
              .map((user) => {
                const events = (usageData[user.email] || []).filter((e) => e.time > startTime && e.time <= endTime);
                const visits = new Set(events.map((e) => e.convo_id).filter(Boolean)).size;
                return { Name: user.name || '—', Email: user.email, Visits: visits };
              })
              .sort((a, b) => b.Visits - a.Visits);
            const type = isNewDataset ? 'New' : 'Returning';
            setChartClickDetail({ title: `${type} Users – ${periodLabel}`, rows, columns: ['Name', 'Email', 'Visits'] });

          }

          // Clicking a mini-chart also opens it fullscreen
          if (!fullscreenChart) setFullscreenChart(chartKey);
        };

        const makeOptions = (chartKey, fullscreen, extraOpts = {}) => ({
          responsive: true,
          maintainAspectRatio: !fullscreen,
          onClick: getClickHandler(chartKey),
          ...extraOpts,
        });

        const CHARTS = [
          {
            key: 'onboarding',
            title: 'Total Users Onboarded',
            description: 'Cumulative users who have completed their first session — tracks how fast the platform is growing in reach.',
            getStats: () => {
              const data = computeOnboardingChartData().datasets[0].data;
              const latest = data[data.length - 1] || 0;
              const prev = data.length >= 2 ? data[data.length - 2] : 0;
              const newLast = latest - prev;
              const growth = prev > 0 ? Math.round((latest - prev) / prev * 100) : 0;
              return [
                { value: latest, label: 'Total Onboarded', color: '#4e73df' },
                { value: `+${newLast}`, label: `New This Period`, color: '#1cc88a' },
                { value: `${growth >= 0 ? '+' : ''}${growth}%`, label: 'Period Growth', color: growth >= 0 ? '#1cc88a' : '#e74a3b' },
              ];
            },
            render: (fullscreen) => (
              <Line
                data={computeOnboardingChartData()}
                options={makeOptions('onboarding', fullscreen, {
                  legend: { display: false },
                  scales: {
                    xAxes: [{ ticks: { maxRotation: 45, fontSize: fullscreen ? 12 : 10 } }],
                    yAxes: [{ ticks: { beginAtZero: true } }],
                  },
                })}
              />
            ),
          },
          {
            key: 'usage',
            title: 'Usage Over Time – All Clinics',
            description: 'Total visits and transcription hours per period — confirms engagement is keeping pace with user growth.',
            getStats: () => {
              const d = computeUsageOverTimeChartData();
              const last = d.datasets[0].data.length - 1;
              const latestVisits = last >= 0 ? d.datasets[0].data[last] : 0;
              const latestHours = last >= 0 ? d.datasets[1].data[last] : 0;
              const prevVisits = last >= 1 ? d.datasets[0].data[last - 1] : 0;
              const trend = prevVisits > 0 ? Math.round((latestVisits - prevVisits) / prevVisits * 100) : 0;
              return [
                { value: latestVisits, label: 'Visits (last period)', color: '#4e73df' },
                { value: `${latestHours.toFixed(1)}h`, label: 'Hours (last period)', color: '#e74a3b' },
                { value: `${trend >= 0 ? '+' : ''}${trend}%`, label: 'Visit Trend', color: trend >= 0 ? '#1cc88a' : '#e74a3b' },
              ];
            },
            render: (fullscreen) => (
              <Bar
                data={computeUsageOverTimeChartData()}
                options={makeOptions('usage', fullscreen, {
                  scales: {
                    xAxes: [{ ticks: { maxRotation: 45, fontSize: fullscreen ? 12 : 10 } }],
                    yAxes: [
                      { id: 'y-visits', position: 'left', ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Visits' } },
                      { id: 'y-hours', position: 'right', ticks: { beginAtZero: true }, gridLines: { drawOnChartArea: false }, scaleLabel: { display: true, labelString: 'Hours' } },
                    ],
                  },
                })}
              />
            ),
          },
          {
            key: 'adoption',
            title: 'Adoption Status (Sum of Users)',
            description: 'Distribution of users across adoption stages each period — a growing green base signals a maturing cohort.',
            getStats: () => {
              const d = computeAdoptionStatusChartData();
              const last = d.datasets[0].data.length - 1;
              if (last < 0) return [];
              const powerUsers = d.datasets[0].data[last];
              const atRisk = d.datasets[3].data[last];
              const total = d.datasets.reduce((s, ds) => s + (ds.data[last] || 0), 0);
              const rate = total > 0 ? Math.round(powerUsers / total * 100) : 0;
              return [
                { value: powerUsers, label: 'Power Users', color: '#1cc88a' },
                { value: atRisk, label: 'At Risk', color: '#e74a3b' },
                { value: `${rate}%`, label: 'Power User Rate', color: rate >= 30 ? '#1cc88a' : '#f6c23e' },
              ];
            },
            render: (fullscreen) => (
              <Bar
                data={computeAdoptionStatusChartData()}
                options={makeOptions('adoption', fullscreen, {
                  // 'index' mode makes onClick return ALL segments at x position so y-coord resolution works
                  hover: { mode: 'index', intersect: false },
                  scales: {
                    xAxes: [{ stacked: true, ticks: { maxRotation: 45, fontSize: fullscreen ? 12 : 10 } }],
                    yAxes: [{ stacked: true, ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Number of Clinicians' } }],
                  },
                })}
              />
            ),
          },
          {
            key: 'active',
            title: 'Active Users Over Time',
            description: 'Distinct users who engaged each period — shows whether your active base is expanding or plateauing.',
            getStats: () => {
              const data = computeActiveUsersChartData().datasets[0].data;
              const last = data.length - 1;
              const latestActive = last >= 0 ? data[last] : 0;
              const maxActive = Math.max(...data, 0);
              const onboardingData = computeOnboardingChartData().datasets[0].data;
              const latestOnboarded = onboardingData[onboardingData.length - 1] || 0;
              const engRate = latestOnboarded > 0 ? Math.round(latestActive / latestOnboarded * 100) : 0;
              return [
                { value: latestActive, label: 'Active (last period)', color: '#1cc88a' },
                { value: maxActive, label: 'Peak Active', color: '#4e73df' },
                { value: `${engRate}%`, label: 'Engagement Rate', color: engRate >= 50 ? '#1cc88a' : engRate >= 25 ? '#f6c23e' : '#e74a3b' },
              ];
            },
            render: (fullscreen) => (
              <Line
                data={computeActiveUsersChartData()}
                options={makeOptions('active', fullscreen, {
                  legend: { display: false },
                  scales: {
                    xAxes: [{ ticks: { maxRotation: 45, fontSize: fullscreen ? 12 : 10 } }],
                    yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Users' } }],
                  },
                })}
              />
            ),
          },
          {
            key: 'newVsReturning',
            title: 'New vs Returning Users',
            description: 'Breaks each period into first-time and repeat users — high returning bars signal users are coming back habitually.',
            getStats: () => {
              const d = computeNewVsReturningChartData();
              const last = d.datasets[0].data.length - 1;
              const latestNew = last >= 0 ? d.datasets[0].data[last] : 0;
              const latestRet = last >= 0 ? d.datasets[1].data[last] : 0;
              const total = latestNew + latestRet;
              const retRate = total > 0 ? Math.round(latestRet / total * 100) : 0;
              return [
                { value: latestNew, label: 'New Users', color: '#4e73df' },
                { value: latestRet, label: 'Returning', color: '#1cc88a' },
                { value: `${retRate}%`, label: 'Return Rate', color: retRate >= 60 ? '#1cc88a' : retRate >= 40 ? '#f6c23e' : '#e74a3b' },
              ];
            },
            render: (fullscreen) => (
              <Bar
                data={computeNewVsReturningChartData()}
                options={makeOptions('newVsReturning', fullscreen, {
                  scales: {
                    xAxes: [{ stacked: true, ticks: { maxRotation: 45, fontSize: fullscreen ? 12 : 10 } }],
                    yAxes: [{ stacked: true, ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Users' } }],
                  },
                })}
              />
            ),
          },
          {
            key: 'providerAvgs',
            title: 'Avg Visits & Hrs per User (All Users)',
            description: 'Per period: average visits per active user (left) and average hours transcribed per active user (right) — unaffected by label filters.',
            getStats: () => {
              const d = computeAvgPerUserChartData();
              const visitData = d.datasets[0]?.data || [];
              const hrsData = d.datasets[1]?.data || [];
              const last = visitData.length - 1;
              const latestVisits = last >= 0 ? visitData[last] : 0;
              const latestHrs = last >= 0 ? hrsData[last] : 0;
              const peakVisits = Math.max(...visitData, 0);
              return [
                { value: latestVisits, label: 'Latest Avg Visits/User', color: '#4e73df' },
                { value: `${latestHrs}h`, label: 'Latest Avg Hrs/User', color: '#1cc88a' },
                { value: peakVisits, label: 'Peak Avg Visits', color: '#f6c23e' },
              ];
            },
            render: (fullscreen) => (
              <Line
                data={computeAvgPerUserChartData()}
                options={makeOptions('providerAvgs', fullscreen, {
                  scales: {
                    xAxes: [{ ticks: { maxRotation: 45, fontSize: fullscreen ? 12 : 10 } }],
                    yAxes: [
                      { id: 'visits', position: 'left', ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Avg Visits / User' } },
                      { id: 'hrs', position: 'right', ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'Avg Hrs / User' }, gridLines: { drawOnChartArea: false } },
                    ],
                  },
                })}
              />
            ),
          },
          {
            key: 'usageHeatmap',
            title: 'Usage Bubble Heatmap — Time of Day',
            description: 'Each bubble = one 1-hour window. Position: date (x) × hour of day (y). Size: unique visits active in that window.',
            colClass: 'col-md-12',
            controls: () => (
              <Button
                size="sm"
                color={hideHeatmapNoise ? 'warning' : 'secondary'}
                outline={!hideHeatmapNoise}
                style={{ marginBottom: '8px', fontSize: '11px' }}
                onClick={() => setHideHeatmapNoise((v) => !v)}
                title="Hide bubbles with fewer than 10% of the peak visit count"
              >
                {hideHeatmapNoise ? '⚡ Noise filtered (<10% of peak)' : 'Filter noise (<10% of peak)'}
              </Button>
            ),
            getStats: () => {
              const d = computeUsageHeatmapData();
              const points = d.datasets[0]?.data || [];
              const totalBubbles = points.length;
              const maxPoint = points.reduce((best, p) => (p._count > (best?._count || 0) ? p : best), null);
              const peakCount = maxPoint?._count || 0;
              const hourTotals = {};
              points.forEach((p) => { hourTotals[p._hour] = (hourTotals[p._hour] || 0) + p._count; });
              const busiestHour = Object.entries(hourTotals).sort((a, b) => b[1] - a[1])[0]?.[0];
              const busiestLabel = busiestHour !== undefined
                ? (busiestHour == 0 ? 'Midnight' : busiestHour < 12 ? `${busiestHour} AM` : busiestHour == 12 ? 'Noon' : `${busiestHour - 12} PM`)
                : '—';
              return [
                { value: totalBubbles, label: 'Active Hour Windows', color: '#4e73df' },
                { value: peakCount, label: 'Peak Visits (1 hr)', color: '#1cc88a' },
                { value: busiestLabel, label: 'Busiest Hour Overall', color: '#f6c23e' },
              ];
            },
            render: (fullscreen) => {
              // Build a per-day colour array so weekends stand out
              // Plugin draws amber bands directly on canvas for each weekend day —
              // avoids the gridLines.color array alignment issues in Chart.js v2.
              const weekendPlugin = {
                beforeDraw: (chart) => {
                  const xAxis = chart.scales['x-axis-0'];
                  const yAxis = chart.scales['y-axis-0'];
                  if (!xAxis || !yAxis) return;
                  const ctx = chart.ctx;
                  ctx.save();
                  ctx.fillStyle = 'rgba(246,194,62,0.15)';
                  const d = new Date(dateRange.startDate);
                  d.setHours(0, 0, 0, 0);
                  const rangeEnd = dateRange.endDate.getTime();
                  while (d.getTime() <= rangeEnd) {
                    const dow = d.getDay();
                    if (dow === 0 || dow === 6) {
                      const x0 = xAxis.getPixelForValue(d.getTime());
                      const next = new Date(d);
                      next.setDate(next.getDate() + 1);
                      const x1 = xAxis.getPixelForValue(next.getTime());
                      ctx.fillRect(x0, yAxis.top, x1 - x0, yAxis.bottom - yAxis.top);
                    }
                    d.setDate(d.getDate() + 1);
                  }
                  ctx.restore();
                },
              };
              return (
              <Bubble
                data={computeUsageHeatmapData()}
                plugins={[weekendPlugin]}
                options={makeOptions('usageHeatmap', fullscreen, {
                  legend: { display: false },
                  tooltips: {
                    callbacks: {
                      label: (item, data) => {
                        const pt = data.datasets[item.datasetIndex].data[item.index];
                        const d = new Date(pt.x);
                        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        const h = pt._hour;
                        const timeStr = h === 0 ? 'Midnight' : h < 12 ? `${h} AM` : h === 12 ? 'Noon' : `${h - 12} PM`;
                        return `${dateStr} @ ${timeStr}: ${pt._count} visit${pt._count !== 1 ? 's' : ''}`;
                      },
                    },
                  },
                  scales: {
                    xAxes: [{
                      type: 'time',
                      time: {
                        unit: 'day',
                        tooltipFormat: 'MMM D, YYYY',
                        displayFormats: { day: 'M/D' },
                      },
                      ticks: {
                        autoSkip: false,
                        maxRotation: 90,
                        fontSize: fullscreen ? 11 : 9,
                        callback: (val, idx) => idx % 7 === 0 ? val : '',
                      },
                      gridLines: { display: true, color: 'rgba(0,0,0,0.07)', lineWidth: 1 },
                      scaleLabel: { display: true, labelString: 'Date' },
                    }],
                    yAxes: [{
                      ticks: {
                        min: 0, max: 23, stepSize: 1,
                        // chart y=23 → Midnight (top), chart y=0 → 11 PM (bottom)
                        callback: (v) => {
                          const h = 23 - v; // convert chart y back to real hour
                          if (h === 0)  return 'Midnight';
                          if (h === 5)  return '5 AM';
                          if (h === 12) return 'Noon';
                          if (h === 17) return '5 PM';
                          if (h === 23) return '11 PM';
                          return '';
                        },
                      },
                      scaleLabel: { display: true, labelString: 'Hour of Day' },
                    }],
                  },
                })}
              />
              );
            },
          },
        ];

        const activeChart = CHARTS.find((c) => c.key === fullscreenChart);

        return (
          <>
            {/* Compact chart cards */}
            <Card style={{ marginBottom: '20px' }}>
              <CardHeader
                onClick={() => setCollapseCharts(!collapseCharts)}
                style={{ cursor: 'pointer', backgroundColor: '#f7fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <h4 style={{ margin: 0 }}>📈 Usage Charts</h4>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: '4px' }}>
                    {['daily', 'weekly', 'monthly'].map((p) => (
                      <Button key={p} size="sm" color={chartPeriod === p ? 'primary' : 'secondary'} outline={chartPeriod !== p} onClick={() => setChartPeriod(p)}>
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </Button>
                    ))}
                  </div>
                  <i className={`fa fa-chevron-${collapseCharts ? 'down' : 'up'}`} />
                </div>
              </CardHeader>
              <Collapse isOpen={!collapseCharts}>
                <div style={{ padding: '20px' }}>
                  <small style={{ color: '#adb5bd', display: 'block', marginBottom: '12px' }}>
                    Click a bar or data point to drill down · Click <i className="fa fa-expand" /> to expand
                  </small>
                  <Row>
                    {CHARTS.map((chart) => {
                      const stats = chart.getStats ? chart.getStats() : [];
                      return (
                        <div key={chart.key} className={chart.colClass || 'col-md-4'} style={{ marginBottom: '24px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                            <h6 style={{ margin: 0, color: '#5a5c69', fontSize: '13px' }}>{chart.title}</h6>
                            <Button
                              size="sm" color="link"
                              style={{ padding: '0 4px', color: '#aaa', flexShrink: 0 }}
                              title="Expand"
                              onClick={() => { setFullscreenChart(chart.key); setChartClickDetail(null); }}
                            >
                              <i className="fa fa-expand" />
                            </Button>
                          </div>
                          {chart.description && (
                            <p style={{ margin: '0 0 6px 0', fontSize: '11px', color: '#9ba5b0', fontStyle: 'italic', lineHeight: 1.4 }}>
                              {chart.description}
                            </p>
                          )}
                          {chart.controls?.()}
                          <div style={{ cursor: 'pointer' }}>{chart.render(false)}</div>
                          {stats.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #f1f3f5' }}>
                              {stats.map((s, i) => (
                                <div key={i} style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: s.color, lineHeight: 1.1 }}>{s.value}</div>
                                  <div style={{ fontSize: '10px', color: '#adb5bd', marginTop: '2px' }}>{s.label}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </Row>
                </div>
              </Collapse>
            </Card>

            {/* True fullscreen overlay */}
            {fullscreenChart && activeChart && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: '#fff', display: 'flex', flexDirection: 'column' }}>

                {/* Header bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #e9ecef', flexShrink: 0, background: '#f8f9fa' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
                    <div style={{ minWidth: 0 }}>
                      <h5 style={{ margin: 0 }}>{activeChart.title}</h5>
                      {activeChart.description && (
                        <p style={{ margin: '1px 0 0 0', fontSize: '12px', color: '#9ba5b0', fontStyle: 'italic' }}>{activeChart.description}</p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      {['daily', 'weekly', 'monthly'].map((p) => (
                        <Button key={p} size="sm" color={chartPeriod === p ? 'primary' : 'secondary'} outline={chartPeriod !== p}
                          onClick={() => { setChartPeriod(p); setChartClickDetail(null); }}>
                          {p.charAt(0).toUpperCase() + p.slice(1)}
                        </Button>
                      ))}
                    </div>
                    {activeChart.getStats && (() => {
                      const stats = activeChart.getStats();
                      return stats.length > 0 ? (
                        <div style={{ display: 'flex', gap: '24px', flexShrink: 0 }}>
                          {stats.map((s, i) => (
                            <div key={i} style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '20px', fontWeight: 'bold', color: s.color, lineHeight: 1 }}>{s.value}</div>
                              <div style={{ fontSize: '10px', color: '#adb5bd' }}>{s.label}</div>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                    <small style={{ color: '#adb5bd', flexShrink: 0 }}>Click a bar or point to drill down</small>
                  </div>
                  <Button
                    color="link"
                    style={{ fontSize: '22px', lineHeight: 1, color: '#6c757d', padding: '0 6px', textDecoration: 'none' }}
                    onClick={() => { setFullscreenChart(null); setChartClickDetail(null); }}
                    title="Close"
                  >
                    ×
                  </Button>
                </div>

                {/* Body: chart + optional detail side-panel */}
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                  {/* Chart fills remaining space */}
                  <div style={{ flex: 1, padding: '20px', position: 'relative', cursor: 'pointer', minWidth: 0 }}>
                    {activeChart.controls && (
                      <div style={{ position: 'absolute', top: 20, left: 20, zIndex: 1 }}>
                        {activeChart.controls()}
                      </div>
                    )}
                    <div style={{ position: 'absolute', top: activeChart.controls ? 56 : 20, left: 20, right: 20, bottom: 20 }}>
                      {activeChart.render(true)}
                    </div>
                  </div>

                  {/* Detail panel (slides in when a point is clicked) */}
                  {chartClickDetail && (
                    <>
                      {/* Drag handle */}
                      <div
                        onMouseDown={handleResizeMouseDown}
                        title="Drag to resize"
                        style={{
                          width: '14px',
                          flexShrink: 0,
                          cursor: 'col-resize',
                          background: '#f0f2f5',
                          borderLeft: '1px solid #dee2e6',
                          borderRight: '1px solid #dee2e6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          userSelect: 'none',
                        }}
                      >
                        <span style={{ color: '#999', fontSize: '11px', letterSpacing: '-1px' }}>|||</span>
                      </div>

                      <div style={{ width: detailPanelWidth, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fff', minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #e9ecef', background: '#f8f9fa' }}>
                          <strong style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{chartClickDetail.title}</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, marginLeft: '8px' }}>
                            {chartClickDetail.rows.length > 0 && (
                              <Button
                                size="sm" color="success"
                                style={{ padding: '2px 8px', fontSize: '11px' }}
                                title="Download CSV"
                                onClick={() => {
                                  const { columns, rows, title } = chartClickDetail;
                                  let csv = columns.map((c) => `"${c}"`).join(',') + '\n';
                                  rows.forEach((row) => {
                                    csv += columns.map((c) => `"${String(row[c] ?? '')}"`).join(',') + '\n';
                                  });
                                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.csv`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                }}
                              >
                                <i className="fa fa-download" />
                              </Button>
                            )}
                            <Button close onClick={() => setChartClickDetail(null)} />
                          </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                          {chartClickDetail.rows.length === 0 ? (
                            <p style={{ padding: '16px', color: '#6c757d', margin: 0, fontSize: '13px' }}>No data for this period.</p>
                          ) : (
                            <table style={{ margin: 0, fontSize: '12px', width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                              <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa' }}>
                                <tr>
                                  {chartClickDetail.columns.map((col) => (
                                    <th key={col} style={{ padding: '6px 10px', borderBottom: '1px solid #e9ecef', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {chartClickDetail.rows.map((row, i) => (
                                  <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                    {chartClickDetail.columns.map((col) => (
                                      <td key={col} style={{ padding: '5px 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(row[col] ?? '')}>
                                        {row[col]}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                        <div style={{ padding: '8px 14px', borderTop: '1px solid #e9ecef', fontSize: '12px', color: '#6c757d', background: '#f8f9fa' }}>
                          {chartClickDetail.rows.length} {chartClickDetail.rows.length === 1 ? 'user' : 'users'}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Adoption Breakdown */}
      {Object.keys(usageData).length > 0 && (
        <Card style={{ marginBottom: '20px' }}>
          <CardHeader
            onClick={() => setCollapseBreakdown(!collapseBreakdown)}
            style={{
              cursor: 'pointer',
              backgroundColor: '#f7fafc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h4 style={{ margin: 0 }}>
              🎯 Adoption Breakdown
              <i
                className="fa fa-info-circle"
                style={{ marginLeft: '8px', fontSize: '14px' }}
                title={adoptionTooltipText}
              ></i>
            </h4>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                color="success"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  exportAdoptionBreakdownToCsv();
                }}
              >
                <i
                  className="fa fa-download"
                  style={{ marginRight: '5px' }}
                ></i>
                Export CSV
              </Button>
              <i
                className={`fa fa-chevron-${collapseBreakdown ? 'up' : 'down'}`}
              ></i>
            </div>
          </CardHeader>
          <Collapse isOpen={collapseBreakdown}>
            <div style={{ padding: '20px' }}>
              <Row>
                <div className="col-md-2">
                  <Badge
                    color="info"
                    pill
                    style={{ fontSize: '15px', padding: '10px 16px' }}
                  >
                    Invited: {stats.invited}
                  </Badge>
                </div>
                <div className="col-md-2">
                  <Badge
                    color="info"
                    pill
                    style={{ fontSize: '15px', padding: '10px 16px' }}
                  >
                    Trying It Out: {stats.tryingItOut}
                  </Badge>
                </div>
                <div className="col-md-3">
                  <Badge
                    color="primary"
                    pill
                    style={{ fontSize: '15px', padding: '10px 16px' }}
                  >
                    Getting the Hang: {stats.gettingTheHang}
                  </Badge>
                </div>
                <div className="col-md-2">
                  <Badge
                    color="success"
                    pill
                    style={{ fontSize: '15px', padding: '10px 16px' }}
                  >
                    Power User: {stats.powerUser}
                  </Badge>
                </div>
                <div className="col-md-3">
                  <Badge
                    color="danger"
                    pill
                    style={{ fontSize: '15px', padding: '10px 16px' }}
                  >
                    At Risk: {stats.potentiallyFailed + stats.churned}
                  </Badge>
                </div>
              </Row>
            </div>
          </Collapse>
        </Card>
      )}

      {/* Label Summary Table */}
      {Object.keys(usageData).length > 0 &&
        Object.keys(labelStats).length > 0 && (
          <Card style={{ marginBottom: '20px' }}>
            <CardHeader
              onClick={() => setCollapseLabelSummary(!collapseLabelSummary)}
              style={{
                cursor: 'pointer',
                backgroundColor: '#f7fafc',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h4 style={{ margin: 0 }}>
                🏷️ Summary by Label
                {selectedLabels.length > 0 && (
                  <small
                    style={{
                      marginLeft: '10px',
                      color: '#6c757d',
                      fontWeight: 'normal',
                    }}
                  >
                    (Filtered: {selectedLabels.join(', ')})
                  </small>
                )}
              </h4>
              <div
                style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
              >
                <Button
                  color="success"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    exportLabelSummaryToCsv();
                  }}
                >
                  <i
                    className="fa fa-download"
                    style={{ marginRight: '5px' }}
                  ></i>
                  Export CSV
                </Button>
                <i
                  className={`fa fa-chevron-${collapseLabelSummary ? 'up' : 'down'}`}
                ></i>
              </div>
            </CardHeader>
            <Collapse isOpen={collapseLabelSummary}>
              <div style={{ padding: '20px' }}>
                <Table striped responsive>
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Users</th>
                      <th>Total Visits</th>
                      <th>Avg Visits</th>
                      <th>Total Hours</th>
                      <th>Avg Hours</th>
                      <th>Power Users</th>
                      <th>Getting Hang</th>
                      <th>Trying Out</th>
                      <th>At Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(labelStats)
                      .sort((a, b) => b[1].userCount - a[1].userCount) // Sort by user count descending
                      .map(([label, stats]) => (
                        <tr key={label}>
                          <td>
                            <Badge color="info" pill>
                              {label}
                            </Badge>
                          </td>
                          <td>
                            <strong>{stats.userCount}</strong>
                          </td>
                          <td>{stats.totalVisits}</td>
                          <td>{stats.avgVisits}</td>
                          <td>{stats.totalHours.toFixed(1)} hrs</td>
                          <td>{stats.avgHours} hrs</td>
                          <td>
                            <Badge color="success">{stats.powerUser}</Badge>
                          </td>
                          <td>
                            <Badge color="primary">
                              {stats.gettingTheHang}
                            </Badge>
                          </td>
                          <td>
                            <Badge color="info">{stats.tryingItOut}</Badge>
                          </td>
                          <td>
                            <Badge color="danger">
                              {stats.potentiallyFailed + stats.churned}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            </Collapse>
          </Card>
        )}

      {/* User Details Table */}
      {Object.keys(usageData).length > 0 && (
        <Card style={{ marginBottom: '20px' }}>
          <CardHeader
            onClick={() => setCollapseUserDetails(!collapseUserDetails)}
            style={{
              cursor: 'pointer',
              backgroundColor: '#f7fafc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h4 style={{ margin: 0 }}>👥 User Activity Details</h4>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                color="success"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  exportUserDetailsToCsv();
                }}
              >
                <i
                  className="fa fa-download"
                  style={{ marginRight: '5px' }}
                ></i>
                Export CSV
              </Button>
              <i
                className={`fa fa-chevron-${collapseUserDetails ? 'up' : 'down'}`}
              ></i>
            </div>
          </CardHeader>
          <Collapse isOpen={collapseUserDetails}>
            <div style={{ padding: '20px' }}>
              <Table striped responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Labels</th>
                    <th>Visits</th>
                    <th>Hours Transcribed</th>
                    <th>Last Activity</th>
                    <th>Days Since Last Use</th>
                    <th>Adoption Stage <small style={{ color: '#adb5bd', fontWeight: 'normal' }}>(rolling 30d)</small></th>
                  </tr>
                </thead>
                <tbody>
                  {filterUsersByLabels(users)
                    .map((user) => {
                      const userData = usageData[user.email] || [];
                      const metrics = calculateUserMetrics(userData);

                      const adoption = computeUserAdoption({
                        credit_usage: metrics.rollingVisits,
                        updated_at: metrics.lastEvent,
                      });

                      const lastEventDate = metrics.lastEvent
                        ? normalizeTimestampToDate(metrics.lastEvent)
                        : null;
                      const daysSince = lastEventDate
                        ? daysDiffFromToday(lastEventDate)
                        : null;

                      return {
                        ...user,
                        ...metrics,
                        adoption,
                        daysSince,
                      };
                    })
                    .sort((a, b) => {
                      // Sort by adoption priority (power users first, at risk last)
                      const priority = {
                        'Power User': 1,
                        'Getting the Hang of It': 2,
                        'Trying It Out': 3,
                        Invited: 4,
                        'Potentially Failed Onboarding': 5,
                        Churned: 6,
                      };
                      const aPriority = priority[a.adoption.label] || 99;
                      const bPriority = priority[b.adoption.label] || 99;
                      if (aPriority !== bPriority) return aPriority - bPriority;
                      // Secondary sort by visits (descending)
                      return b.visits - a.visits;
                    })
                    .map((user) => (
                      <tr key={user.email}>
                        <td>{user.name || '-'}</td>
                        <td>{user.email}</td>
                        <td>
                          {user.labels && user.labels.length > 0
                            ? user.labels.map((label, idx) => (
                                <Badge
                                  key={idx}
                                  color="info"
                                  pill
                                  style={{
                                    marginRight: '4px',
                                    marginBottom: '2px',
                                  }}
                                >
                                  {label}
                                </Badge>
                              ))
                            : '-'}
                        </td>
                        <td>
                          <strong>{user.visits}</strong>
                        </td>
                        <td>{user.hoursTranscribed.toFixed(2)} hrs</td>
                        <td>
                          {user.lastEvent
                            ? formatLastUsedPretty(user.lastEvent)
                            : 'Never'}
                        </td>
                        <td>
                          {user.daysSince !== null
                            ? `${user.daysSince} day${user.daysSince !== 1 ? 's' : ''}`
                            : '-'}
                        </td>
                        <td>
                          <Badge color={user.adoption.color}>
                            {user.adoption.label}
                          </Badge>
                          {user.wowTrend === 'up' && (
                            <span title="More visits this week vs last week" style={{ marginLeft: 5, color: '#1cc88a', fontSize: 13 }}>↑</span>
                          )}
                          {user.wowTrend === 'down' && (
                            <span title="Fewer visits this week vs last week" style={{ marginLeft: 5, color: '#e74a3b', fontSize: 13 }}>↓</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          </Collapse>
        </Card>
      )}

      {/* Calendar View */}
      {Object.keys(usageData).length > 0 && (
        <Card style={{ marginBottom: '20px' }}>
          <CardHeader
            onClick={() => setCollapseCalendar(!collapseCalendar)}
            style={{
              cursor: 'pointer',
              backgroundColor: '#f7fafc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h4 style={{ margin: 0 }}>
              📅 Daily Visits Calendar
              {selectedLabels.length > 0 && (
                <small
                  style={{
                    marginLeft: '10px',
                    color: '#6c757d',
                    fontWeight: 'normal',
                  }}
                >
                  (Filtered: {selectedLabels.join(', ')})
                </small>
              )}
            </h4>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Button
                color="success"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  exportCalendarToCsv();
                }}
              >
                <i
                  className="fa fa-download"
                  style={{ marginRight: '5px' }}
                ></i>
                Export CSV
              </Button>
              <i
                className={`fa fa-chevron-${collapseCalendar ? 'up' : 'down'}`}
              ></i>
            </div>
          </CardHeader>
          <Collapse isOpen={collapseCalendar}>
            <div style={{ padding: '20px' }}>
              {/* View Mode Toggle */}
              <div
                style={{
                  marginBottom: '15px',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                }}
              >
                <Label style={{ margin: 0, fontWeight: 'bold' }}>
                  View by:
                </Label>
                <Button
                  color={
                    calendarViewMode === 'labels' ? 'primary' : 'secondary'
                  }
                  size="sm"
                  onClick={() => setCalendarViewMode('labels')}
                >
                  🏷️ Labels
                </Button>
                <Button
                  color={calendarViewMode === 'users' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setCalendarViewMode('users')}
                >
                  👤 Individual Users
                </Button>
              </div>

              {/* Month Navigation */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                }}
              >
                <Button color="primary" size="sm" onClick={previousMonth}>
                  <i className="fa fa-chevron-left"></i> Previous
                </Button>
                <div
                  style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
                >
                  <h5 style={{ margin: 0 }}>
                    {calendarMonth.toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </h5>
                  <Button color="secondary" size="sm" onClick={goToToday}>
                    Today
                  </Button>
                </div>
                <Button color="primary" size="sm" onClick={nextMonth}>
                  Next <i className="fa fa-chevron-right"></i>
                </Button>
              </div>

              {/* Calendar Grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '2px',
                  backgroundColor: '#e9ecef',
                  padding: '2px',
                  borderRadius: '4px',
                }}
              >
                {/* Day headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                  (day) => (
                    <div
                      key={day}
                      style={{
                        padding: '10px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        backgroundColor: '#f8f9fa',
                        fontSize: '14px',
                      }}
                    >
                      {day}
                    </div>
                  ),
                )}

                {/* Calendar days */}
                {calendarDays.map((dayObj, index) => {
                  if (!dayObj) {
                    return (
                      <div
                        key={`empty-${index}`}
                        style={{
                          padding: '10px',
                          minHeight: '80px',
                          backgroundColor: '#fff',
                        }}
                      ></div>
                    );
                  }

                  const isToday =
                    dayObj.dateKey === new Date().toISOString().split('T')[0];
                  const hasVisits = dayObj.totalVisits > 0;
                  const entityEntries = Object.entries(
                    dayObj.entityVisits,
                  ).sort((a, b) => b[1] - a[1]);

                  // Color intensity based on total visit count
                  let backgroundColor = '#fff';
                  if (hasVisits) {
                    if (dayObj.totalVisits >= 20)
                      backgroundColor = '#2dce89'; // Dark green
                    else if (dayObj.totalVisits >= 10)
                      backgroundColor = '#5ae4a8'; // Medium green
                    else if (dayObj.totalVisits >= 5)
                      backgroundColor = '#a3f5d0'; // Light green
                    else backgroundColor = '#d4f5e9'; // Very light green
                  }

                  return (
                    <div
                      key={dayObj.dateKey}
                      style={{
                        padding: '8px',
                        minHeight: '120px',
                        backgroundColor: backgroundColor,
                        border: isToday ? '2px solid #5e72e4' : 'none',
                        borderRadius: '4px',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: isToday ? 'bold' : 'normal',
                          marginBottom: '8px',
                          paddingBottom: '4px',
                          borderBottom: hasVisits
                            ? '1px solid rgba(0,0,0,0.1)'
                            : 'none',
                        }}
                      >
                        {dayObj.day}
                      </div>
                      {hasVisits && (
                        <div style={{ fontSize: '11px', lineHeight: '1.4' }}>
                          {entityEntries.map(([entity, count]) => (
                            <div
                              key={entity}
                              style={{
                                marginBottom: '2px',
                                color:
                                  dayObj.totalVisits >= 10 ? '#fff' : '#2c3e50',
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontWeight: '500',
                              }}
                            >
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  marginRight: '4px',
                                  maxWidth: '70%',
                                }}
                                title={entity}
                              >
                                {calendarViewMode === 'users'
                                  ? users.find((u) => u.email === entity)
                                      ?.name || entity.split('@')[0]
                                  : entity}
                              </span>
                              <span style={{ fontWeight: 'bold' }}>
                                {count}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend and Info */}
              <div
                style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                }}
              >
                <div
                  style={{
                    marginBottom: '10px',
                    fontSize: '13px',
                    color: '#495057',
                  }}
                >
                  <strong>How to read:</strong> Each day shows{' '}
                  {calendarViewMode === 'labels' ? 'label names' : 'user names'}{' '}
                  with their visit counts. Background color indicates total
                  visits for the day.
                  {calendarViewMode === 'users' &&
                    ' Hover over truncated names to see full email.'}
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '15px',
                    alignItems: 'center',
                    fontSize: '13px',
                    flexWrap: 'wrap',
                  }}
                >
                  <strong>Background Color Legend:</strong>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#d4f5e9',
                        border: '1px solid #ccc',
                      }}
                    ></div>
                    <span>1-4 total</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#a3f5d0',
                        border: '1px solid #ccc',
                      }}
                    ></div>
                    <span>5-9 total</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#5ae4a8',
                        border: '1px solid #ccc',
                      }}
                    ></div>
                    <span>10-19 total</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#2dce89',
                        border: '1px solid #ccc',
                      }}
                    ></div>
                    <span>20+ total</span>
                  </div>
                  <div
                    style={{
                      marginLeft: 'auto',
                      fontStyle: 'italic',
                      color: '#6c757d',
                    }}
                  >
                    🔵 Blue border = Today
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </Card>
      )}

      {Object.keys(usageData).length === 0 && !loading && (
        <div className="alert alert-info">
          No usage data loaded. Click "Generate Report", then View the report
          under Recent Reports once it is ready.
        </div>
      )}

      {/* Bulk Add Labels Modal */}
      <Modal
        isOpen={showLabelsModal}
        toggle={() => setShowLabelsModal(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setShowLabelsModal(false)}>
          Bulk Add Labels to Users
        </ModalHeader>
        <ModalBody>
          <div style={{ marginBottom: '20px' }}>
            <h6>Instructions:</h6>
            <ul style={{ fontSize: '14px', color: '#6c757d' }}>
              <li>
                Upload a CSV file with two columns: <strong>Email</strong> and{' '}
                <strong>Labels</strong>
              </li>
              <li>
                The <strong>Labels</strong> column should contain{' '}
                <strong>semicolon-separated</strong> labels (e.g., "label1;
                label2; label3")
              </li>
              <li>
                Use semicolons (;) to separate multiple labels, not commas
              </li>
              <li>Example CSV format:</li>
            </ul>
            <pre
              style={{
                background: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px',
                fontSize: '12px',
              }}
            >
              {`Email,Labels
user1@example.com,power_user; active
user2@example.com,needs_support; trial
user3@example.com,Buffalo; Power; Gone`}
            </pre>
            <small style={{ color: '#17a2b8', fontStyle: 'italic' }}>
              💡 Tip: Use semicolons (;) to separate multiple labels. Individual
              labels can contain commas if needed.
            </small>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                marginBottom: '10px',
              }}
            >
              <Button
                color="success"
                size="sm"
                onClick={handleDownloadSampleCsv}
                disabled={labelsProcessing || users.length === 0}
              >
                <i
                  className="fa fa-download"
                  style={{ marginRight: '5px' }}
                ></i>
                Download Template CSV
              </Button>
              <small style={{ color: '#6c757d' }}>
                {users.length > 0
                  ? `Template will include ${users.length} user${users.length !== 1 ? 's' : ''}`
                  : 'Load users first to download template'}
              </small>
            </div>
            <Input
              type="file"
              accept=".csv"
              onChange={handleLabelsFileUpload}
              disabled={labelsProcessing}
            />
          </div>

          {labelsCsvData.length > 0 && (
            <div>
              <h6>Preview ({labelsCsvData.length} users):</h6>
              <div
                style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                }}
              >
                <Table striped size="sm" style={{ marginBottom: 0 }}>
                  <thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      background: '#fff',
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th style={{ width: '40%' }}>Email</th>
                      <th style={{ width: '60%' }}>Labels</th>
                    </tr>
                  </thead>
                  <tbody>
                    {labelsCsvData.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.email}</td>
                        <td>
                          {item.labels.map((label, labelIdx) => (
                            <Badge
                              key={labelIdx}
                              color="info"
                              pill
                              style={{
                                marginRight: '5px',
                                marginBottom: '3px',
                              }}
                            >
                              {label}
                            </Badge>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {labelsCsvData.length === 0 && (
            <div className="alert alert-info" style={{ marginTop: '10px' }}>
              No data loaded. Please upload a CSV file.
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShowLabelsModal(false);
              setLabelsCsvData([]);
            }}
            disabled={labelsProcessing}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleBulkLabelsSubmit}
            disabled={labelsCsvData.length === 0 || labelsProcessing}
          >
            {labelsProcessing
              ? 'Processing...'
              : `Add Labels to ${labelsCsvData.length} Users`}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AdoptionDashboard;
